import {formatAsyncQuestionAnswer,type AsyncQuestionAnswer,type ChatMessage,type CodexNotification,type ThreadRecord} from '@reados/contracts';

export interface AsyncQuestionAnswerRequest extends AsyncQuestionAnswer {requestId:string}
export interface AsyncQuestionAnswerAccepted {
 accepted:true;messageID:string;threadId:string;turnId:string;requestId:string;responseTurnId:string;
}
export interface PreparedAsyncQuestionAnswer {
 ownerThreadId:string;
 answer:AsyncQuestionAnswer;
}
const uuidV4=/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface AsyncQuestionAnswerHistory {ownerThreadId:string;events:CodexNotification[]}
/** Accepted-answer receipts outlive the bounded live tool-event window. Late
 * history can add evidence, but cannot reopen an already answered question. */
export function mergeAsyncQuestionAnswerHistory(current:AsyncQuestionAnswerHistory|null,incoming:readonly CodexNotification[],ownerThreadId:string):AsyncQuestionAnswerHistory {
 const receipts=new Map<string,CodexNotification>();
 for(const event of [...(current?.ownerThreadId===ownerThreadId?current.events:[]),...incoming]){
  const p=event.params;
  if(event.method!=='reados/asyncQuestionAnswered'||p.ownerThreadId!==ownerThreadId
   ||![p.threadId,p.turnId,p.messageID,p.responseTurnId].every(value=>typeof value==='string'&&value.length>0)
   ||typeof p.requestId!=='string'||!uuidV4.test(p.requestId)||typeof p.answerSha256!=='string'||!/^[a-f0-9]{64}$/.test(p.answerSha256)
   ||typeof p.acceptedAtMilliseconds!=='number'||!Number.isFinite(p.acceptedAtMilliseconds)||p.acceptedAtMilliseconds<=0)continue;
  const identity=JSON.stringify([p.threadId,p.turnId,p.messageID]);
  if(!receipts.has(identity))receipts.set(identity,event);
 }
 return {ownerThreadId,events:[...receipts.values()]};
}
export function asyncQuestionAnswerEventsForThread(history:AsyncQuestionAnswerHistory|null,ownerThreadId:string|null):CodexNotification[]{
 return history?.ownerThreadId===ownerThreadId?history.events:[];
}

/** The iframe identifies a question, never a destination conversation. Resolve
 * its exact source again from the current owner's unchanged native messages. */
export function prepareAsyncQuestionAnswer(thread:ThreadRecord|null,messages:ChatMessage[],action:AsyncQuestionAnswer):PreparedAsyncQuestionAnswer {
 const matches=messages.filter(message=>message.id===action.messageID&&message.threadId===action.threadId&&message.turnId===action.turnId);
 if(!thread?.id||!thread.codexThreadId||action.threadId!==thread.codexThreadId||!action.messageID||!action.threadId||!action.turnId||matches.length!==1
   ||matches[0].status==='error'||!formatAsyncQuestionAnswer(matches[0],action.answers))
  throw new Error('无法确认这个问题的来源或回答内容，请刷新对话后重试。');
 return {ownerThreadId:thread.id,answer:{messageID:action.messageID,threadId:action.threadId,turnId:action.turnId,answers:action.answers.map(answer=>answer.trim())}};
}

export interface AsyncQuestionAnswerOptions {
 prepared:PreparedAsyncQuestionAnswer;
 readKey:(identity:string)=>unknown;
 writeKey:(identity:string,key:string)=>void;
 validate:()=>void;
 submit:(ownerThreadId:string,request:AsyncQuestionAnswerRequest)=>Promise<AsyncQuestionAnswerAccepted>;
}

/** Retain only a request key under a hash of its exact source and answers. An
 * explicit retry after a lost response reuses the server's durable receipt;
 * neither changing owners nor a failed steer can start another turn locally. */
export function createAsyncQuestionAnswerOperation(){
 let flight:{identity:string;promise:Promise<void>}|null=null;
 return {
  get busy(){return flight!==null;},
  run(options:AsyncQuestionAnswerOptions):Promise<void>{
   const {prepared,readKey,writeKey,validate,submit}=options;
   const identity=JSON.stringify([prepared.ownerThreadId,prepared.answer.threadId,prepared.answer.turnId,prepared.answer.messageID,prepared.answer.answers]);
   if(flight)return flight.identity===identity?flight.promise:Promise.reject(new Error('正在提交另一个回答，请稍后重试。'));
   const promise=Promise.resolve().then(async()=>{
    const digest=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(identity));
    const key=[...new Uint8Array(digest)].map(byte=>byte.toString(16).padStart(2,'0')).join('');
    validate();
    const retained=readKey(key),requestId=typeof retained==='string'&&uuidV4.test(retained)?retained:crypto.randomUUID();
    writeKey(key,requestId);
    const response=await submit(prepared.ownerThreadId,{...prepared.answer,requestId});
    if(response?.accepted!==true||response.requestId!==requestId||response.messageID!==prepared.answer.messageID
      ||response.threadId!==prepared.answer.threadId||response.turnId!==prepared.answer.turnId
      ||typeof response.responseTurnId!=='string'||!response.responseTurnId)
     throw new Error('服务端尚未确认这次回答，请重试。');
   }).finally(()=>{flight=null;});
   flight={identity,promise};return promise;
  },
 };
}
